import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useAxiosWrapper } from '../../services/axiosWrapper';
import { authAtom } from '../usuarios/AuthState';
import { history } from '../../helpers/history';
import { solicitacoesAtom, solicitacaoAtualAtom } from './SolicitacaoState';
import { IComentarioRequest, ISolicitacao, ISolicitacaoRequest } from '../../commons/types/solicitacoes';

function useSolicitacaoActions() {
  const axiosWrapper = useAxiosWrapper();
  const authState = useRecoilValue(authAtom);
  const setAuth = useSetRecoilState(authAtom);
  const setSolicitacoes = useSetRecoilState(solicitacoesAtom);
  const setSolicitacaoAtual = useSetRecoilState(solicitacaoAtualAtom);
  
  // Função para obter o ID do assessorado do usuário autenticado
  const getAssessoradoId = (): number => {
    if (!authState || !authState.id) {
      throw new Error('Usuário não autenticado ou ID não disponível');
    }
    return authState.id;
  };
  
  // Função para obter o ID da comanda do usuário autenticado
  const getComandaId = (): number => {
    if (!authState || !authState.comanda || !authState.comanda.id) {
      throw new Error('Comanda não disponível para o usuário autenticado');
    }
    return authState.comanda.id;
  };

  return {
    listarSolicitacoes,
    obterSolicitacao,
    criarSolicitacao,
    adicionarComentario,
    getAssessoradoId,
    getComandaId,
  };

  async function listarSolicitacoes() {
    const assessoradoId = getAssessoradoId();
    const url = `${process.env.REACT_APP_API_URL}/v1/kanban/assessorados/solicitacoes?assessoradoId=${assessoradoId}`;

    try {
      const response = await axiosWrapper.get(url);
      setSolicitacoes(response);
      return response;
    } catch (error) {
      if (error === 'Usuário inválido') {
        localStorage.removeItem('usuario');
        setAuth(null);
        history.push('/login');
      }
      throw error;
    }
  }

  async function obterSolicitacao(id: number) {
    const assessoradoId = getAssessoradoId();
    const url = `${process.env.REACT_APP_API_URL}/v1/kanban/assessorados/solicitacoes/${id}?assessoradoId=${assessoradoId}`;

    try {
      const response = await axiosWrapper.get(url);
      setSolicitacaoAtual(response);
      return response;
    } catch (error) {
      if (error === 'Usuário inválido') {
        localStorage.removeItem('usuario');
        setAuth(null);
        history.push('/login');
      }
      throw error;
    }
  }

  async function criarSolicitacao(dados: ISolicitacaoRequest): Promise<ISolicitacao> {
    const url = `${process.env.REACT_APP_API_URL}/v1/kanban/assessorados/solicitacoes`;

    try {
      // Obter IDs do usuário autenticado se não fornecidos
      const assessoradoId = dados.assessoradoId || getAssessoradoId();
      const comandaId = dados.comandaId || getComandaId();
      
      // Criar FormData para envio multipart/form-data
      const formData = new FormData();
      
      // Adicionar campos visíveis para o assessorado
      formData.append('Titulo', dados.titulo);
      formData.append('Descricao', dados.descricao);
      formData.append('AssessoradoId', assessoradoId.toString());
      formData.append('ComandaId', comandaId.toString());
      
      // Adicionar anexos, se houver
      if (dados.anexos && dados.anexos.length > 0) {
        dados.anexos.forEach(file => {
          formData.append('Anexos', file);
        });
      }

      const response = await axiosWrapper.postFormData(url, formData);

      // Atualizar a lista de solicitações após criar uma nova
      await listarSolicitacoes();
      
      return response;
    } catch (error) {
      if (error === 'Usuário inválido') {
        localStorage.removeItem('usuario');
        setAuth(null);
        history.push('/login');
      }
      throw error;
    }
  }

  async function adicionarComentario(dados: IComentarioRequest) {
    const url = `${process.env.REACT_APP_API_URL}/v1/kanban/assessorados/solicitacoes/${dados.solicitacaoId}/comentarios`;

    try {
      // Obter ID do assessorado se não fornecido
      const assessoradoId = dados.assessoradoId || getAssessoradoId();
      
      // Criar FormData para envio multipart/form-data
      const formData = new FormData();
      
      // Adicionar conteúdo do comentário e ID do assessorado
      formData.append('Comentario', dados.conteudo);
      formData.append('AssessoradoId', assessoradoId.toString());
      
      // Adicionar anexos, se houver
      if (dados.anexos && dados.anexos.length > 0) {
        dados.anexos.forEach(file => {
          formData.append('Anexos', file);
        });
      }

      const response = await axiosWrapper.postFormData(url, formData);

      // Atualizar a solicitação atual após adicionar um comentário
      if (dados.solicitacaoId) {
        await obterSolicitacao(dados.solicitacaoId);
      }
      
      return response;
    } catch (error) {
      if (error === 'Usuário inválido') {
        localStorage.removeItem('usuario');
        setAuth(null);
        history.push('/login');
      }
      throw error;
    }
  }
}

export { useSolicitacaoActions };
